import React from 'react';
import { useTranslation } from 'react-i18next';

interface ReviewPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * Composant de pagination pour les avis
 */
const ReviewPagination: React.FC<ReviewPaginationProps> = ({ 
  currentPage, 
  totalPages, 
  onPageChange 
}) => {
  const { t } = useTranslation();
  
  // Si une seule page, ne pas afficher la pagination
  if (totalPages <= 1) return null;
  
  // Générer les numéros de page à afficher
  const getPageNumbers = () => {
    const pages = [];
    
    // Toujours afficher la première page
    pages.push(1);
    
    // Pages autour de la page courante
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    
    // Toujours afficher la dernière page si plus d'une page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    // Ajouter des ellipses si nécessaire
    const result = [];
    let prevPage = 0;
    
    for (const page of pages) {
      if (page - prevPage > 1) {
        result.push('...');
      }
      result.push(page);
      prevPage = page;
    }
    
    return result;
  };
  
  return (
    <div className="flex justify-center items-center mt-8 space-x-2">
      {/* Bouton précédent */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-1 rounded-md text-sm ${
          currentPage === 1 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
            : 'bg-white text-primary hover:bg-primary/10 border border-gray-200'
        }`}
        aria-label={t('reviews.previous')?.toString() || ''}
      >
        &laquo;
      </button>
      
      {/* Numéros de page */}
      {getPageNumbers().map((page, index) => (
        page === '...' ? (
          <span key={`ellipsis-${index}`} className="px-3 py-1 text-gray-500">...</span>
        ) : (
          <button
            key={`page-${page}`}
            onClick={() => onPageChange(page as number)}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === page 
                ? 'bg-primary text-white' 
                : 'bg-white text-primary hover:bg-primary/10 border border-gray-200'
            }`}
          >
            {page}
          </button>
        )
      ))}
      
      {/* Bouton suivant */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 rounded-md text-sm ${
          currentPage === totalPages 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
            : 'bg-white text-primary hover:bg-primary/10 border border-gray-200'
        }`}
        aria-label={t('reviews.next')?.toString() || ''}
      >
        &raquo;
      </button>
    </div>
  );
};

export default ReviewPagination;
